import { Async, EmptyState, PageHeader } from '../../components/ui/States';
import { StatCard } from '../../components/ui/StatCard';
import { Badge } from '../../components/ui/Badge';
import { useApi } from '../../hooks/useApi';
import { Eye, Heart, Layers, CheckCircle2 } from 'lucide-react';

interface CreatorAnalytics {
  summary: { titles: number; totalViews: number; totalLikes: number; completionRate: number | null };
  titles: {
    id: string;
    title: string;
    status: string;
    views: number;
    likes: number;
    started: number;
    completed: number;
    completionRate: number | null;
  }[];
  revenue: unknown;
  hasData: boolean;
}

const STATUS: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'danger' }> = {
  draft: { label: 'Draft', variant: 'default' },
  pending_review: { label: 'In review', variant: 'warning' },
  published: { label: 'Live', variant: 'success' },
  rejected: { label: 'Needs changes', variant: 'danger' },
};

const pct = (r: number | null | undefined) => (r == null ? '—' : `${Math.round(r * 100)}%`);

export function Performance() {
  const state = useApi<CreatorAnalytics>('/admin/analytics/creator');
  return (
    <div className="space-y-6">
      <PageHeader title="Performance" subtitle="How viewers are watching your titles." />
      <Async state={state}>
        {(d) => (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <StatCard label="Titles" value={d.summary.titles.toLocaleString()} icon={<Layers className="w-5 h-5" />} />
              <StatCard label="Total views" value={d.summary.totalViews.toLocaleString()} icon={<Eye className="w-5 h-5" />} />
              <StatCard label="Total likes" value={d.summary.totalLikes.toLocaleString()} icon={<Heart className="w-5 h-5" />} />
              <StatCard
                label="Finish rate"
                value={pct(d.summary.completionRate)}
                subtitle="Viewers who watched to the end"
                icon={<CheckCircle2 className="w-5 h-5" />}
              />
            </div>

            {!d.hasData ? (
              <EmptyState
                title="No views yet"
                message="Numbers appear here once viewers start watching your titles."
              />
            ) : (
              <div className="border border-[#241b33] rounded-xl overflow-x-auto bg-[#120d1a]">
                <table className="w-full text-left text-xs">
                  <thead className="bg-[#160f22] text-gray-400 font-semibold uppercase tracking-wider text-[10px] border-b border-[#241b33]">
                    <tr>
                      <th className="px-4 py-2.5">Title</th>
                      <th className="px-4 py-2.5">Status</th>
                      <th className="px-4 py-2.5 text-right">Views</th>
                      <th className="px-4 py-2.5 text-right">Likes</th>
                      <th className="px-4 py-2.5 text-right">Started watching</th>
                      <th className="px-4 py-2.5 text-right">Finished</th>
                      <th className="px-4 py-2.5 text-right">Finish rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1e152d] text-gray-300">
                    {d.titles.map((t) => {
                      const s = STATUS[t.status] ?? { label: t.status, variant: 'default' as const };
                      return (
                        <tr key={t.id}>
                          <td className="px-4 py-2.5 font-semibold text-white">{t.title}</td>
                          <td className="px-4 py-2.5">
                            <Badge variant={s.variant}>{s.label}</Badge>
                          </td>
                          <td className="px-4 py-2.5 text-right">{t.views.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right">{t.likes.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right">{t.started.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right">{t.completed.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right">{pct(t.completionRate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-gray-500">Earnings appear here once viewers start buying coins.</p>
          </>
        )}
      </Async>
    </div>
  );
}
